import { Button, Card, Chip, Dialog, DialogActions, DialogContent, Grid, Stack } from '@mui/material'
import DtoDangerTable from 'pgsse/dto/DtoDangerTable'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import { useParams } from 'react-router'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import DtoVisitAction from 'installation/components/campaign/dto/DtoVisitAction'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import useSandreList from 'utils/customHook/useSandreList'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getDate } from 'utils/DateUtil'
import { REDUCER_ACTIONS } from 'pgsse/constant/PgsseConstants'
import CloseIcon from '@mui/icons-material/Close'
import { getBgColor, getIcon, getTitle } from 'pgsse/util/PgsseUtil'
import Select from 'components/forms/Select'
import Input from 'components/forms/Input'
import SimpleTextArea from 'components/forms/SimpleTextArea'
import { forEach } from 'lodash'
import { onChangeDate } from 'utils/FormUtils'
import SmallPicturePanel from 'components/picture/SmallPicturePanel'
import FilePanel from 'components/file/FilePanel'
import StationAction from 'station/actions/StationAction'
import { STATION_TYPE_CONSTANT } from 'station/constants/StationConstants'
import DtoDistributionUnit from 'distributionUnit/dto/DtoDistributionUnit'
import { useUploadFile } from 'utils/customHook/useUploadFile'
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined'
import moment from 'moment'

const CardAction = ({
    action,
    dispatchStateAUD,
    readMode,
    onChange = () => {},
    udi,
    isHighlighted = false,
}) => {
    const {
        pictures,
        files,
    } = useSelector(store => ({
        pictures: store.StationReducer.pictures,
        files: store.StationReducer.files,
    }), shallowEqual)

    const dispatch = useDispatch()
    const uploadFile = useUploadFile(udi.id, udi.code, 'productionUnit')

    const actionPictures = pictures?.filter(p =>
        action.link_docs?.some(d => d.docName === p.name),
    )
    const hasPictures = actionPictures && actionPictures.length > 0

    const actionFiles = files?.filter(f =>
        action.link_docs?.some(d => d.docName === f.name),
    )
    const hasFiles = actionFiles && actionFiles.length > 0

    const onUploadFile = newFileName => {
        onChange({
            ...action,
            link_docs: [
                ...action.link_docs,
                {
                    idUdi: udi.id,
                    idAction: action.idAction,
                    docName: newFileName,
                },
            ],
        })
        dispatch(StationAction.fetchPictures(udi.code, STATION_TYPE_CONSTANT.productionUnit))
        dispatch(StationAction.fetchFiles(udi.code, STATION_TYPE_CONSTANT.productionUnit))
    }

    const deleteAction = () => {
        dispatchStateAUD({ type: REDUCER_ACTIONS.DELETE, payload: action })
    }

    return (
        <Card
            sx={{
                padding: 1,
                border: isHighlighted ? '1px solid black' : '1px solid grey',
                backgroundColor: isHighlighted ? 'lightGray' : 'white',
                transition: 'background-color 0.5s, border-color 0.5s',
            }}
        >
            <Grid container spacing={1}>
                <Grid container size='grow'>
                    <Grid size={4}>
                        <Select
                            label={i18n.dangerGroup}
                            value={action.risk}
                            options={useSandreList(SANDRE.VISITES_ACTIONS_RISQUE)}
                            sortFunc={v => v.code}
                            onChange={value => onChange({ ...action, risk: value })}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            label={i18n.localisation}
                            value={action.localisation}
                            options={useSandreList(SANDRE.VISITES_ACTIONS_SECTEUR)}
                            sortFunc={v => v.code}
                            onChange={value => onChange({ ...action, localisation: value })}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            label={i18n.equipmentType}
                            value={action.equipmentType}
                            options={useSandreList(SANDRE.EQUIPEMENTS_TYPE_ASST)}
                            sortFunc={v => v.code}
                            onChange={value => onChange({ ...action, equipmentType: value })}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            label={i18n.category}
                            value={action.category}
                            options={useSandreList(SANDRE.VISITES_ACTIONS_CATEGORIE)}
                            sortFunc={v => v.code}
                            onChange={value => onChange({ ...action, category: value })}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            label={i18n.responsible}
                            value={action.responsible}
                            options={useSandreList(SANDRE.VISITES_ACTIONS_RESPONSABILITE)}
                            sortFunc={v => v.code}
                            onChange={value => onChange({ ...action, responsible: value })}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            label={i18n.deadline}
                            value={action.deadline}
                            options={useSandreList(SANDRE.VISITES_ACTIONS_ECHEANCE)}
                            sortFunc={v => v.code}
                            onChange={value => onChange({ ...action, deadline: value })}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            label={i18n.interventionType}
                            value={action.interventionType}
                            options={useSandreList(SANDRE.VISITES_ACTIONS_TYPE_INTERVENTION)}
                            sortFunc={v => v.code}
                            onChange={value => onChange({ ...action, interventionType: value })}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid size={8}>
                        <Input
                            title={i18n.comment}
                            value={action.comment}
                            onChange={v => onChange({ ...action, comment: v })}
                            disabled={readMode}
                        />
                    </Grid>
                </Grid>
                {!readMode && (
                    <CloseIcon sx={{ cursor: 'pointer' }} onClick={deleteAction} />
                )}
            </Grid>
            <StyledFieldSet style={{ marginBottom: 10 }}>
                <StyledLegend>{i18n.operatorFeedback}</StyledLegend>
                <Grid container columnSpacing={1}>
                    <Grid size={4}>
                        <Input
                            title={i18n.correctionDate}
                            value={getDate(action.correctionDate)}
                            onChange={v => onChangeDate(v, v2 => onChange({ ...action, correctionDate: v2 }))}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid size={8}>
                        <SimpleTextArea
                            title={i18n.commentCorrection}
                            value={action.commentCorrection}
                            onChange={v => onChange({ ...action, commentCorrection: v })}
                            disabled={readMode}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={1}>
                    {!readMode && (
                        <Grid
                            className='file-field'
                            container
                            size={12}
                            justifyContent='center'
                            direction='column'
                            alignItems='center'
                            sx={{
                                height: '130px',
                                border: 'dashed 2px grey',
                                color: 'grey',
                                '&:hover': {
                                    color: 'black',
                                    border: 'dashed 2px black',
                                },
                            }}
                        >
                            <AddBoxOutlinedIcon sx={{ fontSize: '50px' }} />
                            {i18n.addPictureOrFile}
                            <input
                                accept='.pdf, image/*'
                                type='file'
                                multiple
                                onChange={e => uploadFile(e, onUploadFile)}
                            />
                        </Grid>
                    )}
                    {hasPictures && (
                        <Grid size={hasFiles ? 6 : 12}>
                            <SmallPicturePanel pictures={actionPictures} />
                        </Grid>
                    )}
                    {hasFiles && (
                        <Grid size={hasPictures ? 6 : 12}>
                            <FilePanel
                                code={udi.code}
                                files={actionFiles}
                                hideTitle
                            />
                        </Grid>
                    )}
                </Grid>
            </StyledFieldSet>
        </Card>
    )
}

CardAction.propTypes = {
    action: PropTypes.instanceOf(DtoVisitAction),
    dispatchStateAUD: PropTypes.func,
    readMode: PropTypes.bool,
    onChange: PropTypes.func,
    udi: PropTypes.instanceOf(DtoDistributionUnit),
    isHighlighted: PropTypes.bool,
}

const ActionDialog = ({
    danger = {},
    isOpen = false,
    setIsOpen = () => {},
    stateAUD = {},
    dispatchStateAUD,
    readMode,
}) => {
    const {
        actionElements,
        diagnostic,
        installations,
        contributors,
        installationsTypes,
        distributionUnits,
    } = useSelector(store => ({
        actionElements: store.PgsseReducer.actions,
        diagnostic: store.PgsseReducer.diagnostic,
        installations: store.InstallationReducer.installationsIndex,
        contributors: store.ContributorReducer.contributorsIndex,
        installationsTypes: store.InstallationReducer.installationsTypes,
        distributionUnits: store.DistributionUnitReducer.distributionUnits,
    }), shallowEqual)

    const dispatch = useDispatch()

    const { idUdi } = diagnostic

    const udi = distributionUnits.find(du => du.id === idUdi)

    useEffect(() => {
        if (udi?.code) {
            dispatch(StationAction.fetchPictures(udi.code, STATION_TYPE_CONSTANT.productionUnit))
            dispatch(StationAction.fetchFiles(udi.code, STATION_TYPE_CONSTANT.productionUnit))
        }
    }, [udi?.code])

    const { idDiagnostic } = useParams()
    const structures = useSandreList(SANDRE.PGSSE_STRUCTURE)

    const [modifiedActions, setModifiedActions] = useState({})
    const [lastCreatedAction, setLastCreatedAction] = useState(null)

    const onActionChange = (action) => {
        setModifiedActions(prev => ({
            ...prev,
            [action.creationDate]: action,
        }))
    }

    const actions = useMemo(() => {
        const dangerActions = actionElements.filter(a => a.idDanger === danger.idDanger)

        const allActions = [
            ...stateAUD.addedActions,
            ...dangerActions.filter(a => !stateAUD.deletedActions.includes(a.idAction)),
        ].map(a => {
            const uniqueId = a.creationDate ?? a.idAction
            return modifiedActions[uniqueId]
                || stateAUD.updatedActions.find(ud => ud.idAction === a.idAction)
                || { ...a, uniqueId }
        })

        return allActions.sort((a, b) => b.creationDate - a.creationDate)
    }, [actionElements, danger.idDanger, stateAUD.deletedActions, stateAUD.updatedActions, stateAUD.addedActions, modifiedActions])

    const nbActionsTotal = actions.length
    const completedActions = actions.filter(a => a.correctionDate <= moment().valueOf())

    const addAction = () => {
        const newAction = {
            idDanger: danger.idDanger,
            idDiag: parseInt(idDiagnostic),
            idAction: 0,
            creationDate: moment().valueOf(),
        }

        dispatchStateAUD({
            type: REDUCER_ACTIONS.ADD,
            payload: [newAction],
        })

        setLastCreatedAction(newAction.creationDate)
        setTimeout(() => {
            setLastCreatedAction(null)
        }, 2000)
    }

    useEffect(() => {
        if (!isOpen) {
            setModifiedActions({})
        }
    }, [isOpen])

    const handleSave = () => {
        forEach(modifiedActions, action => {
            dispatchStateAUD({
                type: REDUCER_ACTIONS.UPDATE,
                payload: action,
            })
        })
        setModifiedActions({})
        setIsOpen(false)
    }

    return (
        <Dialog
            open={isOpen}
            maxWidth='lg'
        >
            <DefaultDialogTitle
                title={
                    <Grid container alignItems='center' spacing={2}>
                        {getIcon(danger, contributors, installations, '2rem')}
                        {getTitle(danger, contributors, installations, installationsTypes)}
                    </Grid>
                }
                onClose={() => setIsOpen(false)}
            />
            <DialogContent>
                <Stack spacing={1}>
                    <Card
                        sx={{
                            padding: 1,
                            border: '1px solid grey',
                            position: 'sticky',
                            zIndex: 100,
                            top: 0,
                        }}
                    >
                        <Grid container justifyContent='space-between'>
                            <strong>{i18n.date}: {getDate(danger.creationDate)}</strong>
                            <strong>{i18n.structure}: {structures.find(s => s.code === danger.idStructure)?.name}</strong>
                            <Chip
                                label={<strong>{i18n.risk}: {(danger.severity * danger.frequency) / danger.detectability}</strong>}
                                sx={{
                                    color: 'white',
                                    backgroundColor: getBgColor((danger.severity * danger.frequency) / danger.detectability),
                                    height: '1.5rem',
                                }}
                            />
                            <strong>{i18n.completedActions}: {completedActions.length} / {nbActionsTotal}</strong>
                        </Grid>
                    </Card>
                    {actions.length === 0 ? i18n.noActionForThisDanger : (
                        actions.map(a => (
                            <CardAction
                                key={a.creationDate}
                                action={a}
                                readMode={readMode}
                                dispatchStateAUD={dispatchStateAUD}
                                onChange={onActionChange}
                                udi={udi}
                                isHighlighted={lastCreatedAction === a.creationDate}
                            />
                        ))
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: readMode ? 'end' : 'space-between' }}>
                {!readMode && (
                    <Button variant='contained' onClick={addAction}>
                        {i18n.addAction}
                    </Button>
                )}
                {!readMode && (
                    <Button variant='contained' onClick={handleSave}>
                        {i18n.validate}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

ActionDialog.propTypes = {
    danger: PropTypes.instanceOf(DtoDangerTable),
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    dispatchStateAUD: PropTypes.func,
    stateAUD: PropTypes.shape({}),
    readMode: PropTypes.bool,
}

export default ActionDialog
